import { NavigationBack } from "@/components";
import useGallery from "@/hooks/useGallery";
import useProfile from "@/hooks/useProfile";
import useWebcam from "@/hooks/useWebcam";
import { randFileName } from "@/libraries/helper";
import { polyfillGetUserMedia } from "@/libraries/media";
import { GalleryCameraSearchSchema } from "@/schemas";
import { storageService } from "@/services";
import CachedIcon from "@mui/icons-material/Cached";
import CircleIcon from "@mui/icons-material/Circle";
import RectangleIcon from "@mui/icons-material/Rectangle";
import IconButton from "@mui/material/IconButton";
import { Link, createFileRoute, useRouterState } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import orderBy from "lodash/orderBy";
import { useCallback, useMemo } from "react";

export const Route = createFileRoute("/dashboard/gallery/video")({
	validateSearch: zodSearchValidator(GalleryCameraSearchSchema),
	component: RouteComponent,
});

polyfillGetUserMedia();

function RouteComponent() {
	const navigate = Route.useNavigate();
	const { profile } = useProfile();

	const messageTag = Route.useSearch({
		select: (state) => state.tag,
	});
	const channelName = useRouterState({
		// @ts-expect-error
		select: (s) => s.location.state?.channelName ?? null,
	});

	const { onCaptureImage } = useGallery();

	const onCaptureEnd = useCallback(
		async (blob: Blob) => {
			const fileName = randFileName(".webm");
			const file = new File([blob], fileName, { type: blob.type });
			if (!messageTag && !channelName) {
				await storageService.create([
					{
						file,
						file_name: fileName,
					},
				]);
			} else {
				onCaptureImage(file);
				navigate({
					replace: !!messageTag,
					to: "/dashboard/gallery/view",
					search: {
						tag: messageTag,
					},
					state: {
						channelName,
					},
				});
			}
		},
		[messageTag, navigate, channelName, onCaptureImage],
	);

	const {
		videoRef,
		audio,
		isRecording,
		switchCamera,
		onStartRecord,
		onStopRecord,
	} = useWebcam({
		audio: false,
		mirrored: true,
		onCaptureEnd,
	});

	const currentFileStorage = useMemo(() => {
		if (!profile?.storages) return null;
		const sortedStorages = orderBy(
			profile.storages,
			[(item) => new Date(item.created_at)],
			["desc"],
		);
		return sortedStorages[0];
	}, [profile?.storages]);

	return (
		<div className="relative !p-0 bg-dark h-full overflow-hidden">
			<div className="absolute z-10 top-0 left-0 w-full">
				<div className="container">
					<NavigationBack />
				</div>
			</div>
			<div className="flex flex-col justify-center items-center w-full h-full max-h-dvh">
				<video
					ref={videoRef}
					muted={!audio}
					playsInline
					autoPlay
					className="w-full h-full object-cover"
				/>
			</div>
			<div className="absolute bottom-0 left-0 w-full">
				<div className="flex justify-between items-center container py-6 px-4 bg-dark/65">
					<Link
						to="/dashboard/gallery"
						className="flex-none relative w-12 h-12 rounded-md bg-white"
					>
						{currentFileStorage?.file_id ? (
							<img
								src={currentFileStorage?.file_id}
								alt="captured"
								className="w-full h-full object-cover object-center"
								loading="eager"
							/>
						) : null}
					</Link>
					<IconButton
						color="primary"
						size="large"
						title="Capture"
						className="!border-2 !border-white !border-solid !p-1"
						onClick={isRecording ? onStopRecord : onStartRecord}
					>
						{isRecording ? (
							<RectangleIcon className="!w-14 !h-14 !text-red-500" />
						) : (
							<CircleIcon className="!w-14 !h-14 !text-red-500" />
						)}
					</IconButton>
					<IconButton
						color="primary"
						size="large"
						title="Switch Camera"
						className="!border-2 !border-white !bg-dark/65"
						onClick={switchCamera}
					>
						<CachedIcon className="!text-white" />
					</IconButton>
				</div>
			</div>
		</div>
	);
}
